import { useEffect, useRef } from "react";
import { X } from "lucide-react";
import { Link } from "react-router-dom";

import { brandAssets } from "../../../assets/logoRegistry";
import { siteMetadata } from "../../../content/siteMetadata";
import useMobileMenu from "../../../hooks/useMobileMenu";

const FOCUSABLE_SELECTORS =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const MobileMenu = () => {
  const { isMobileMenuOpen, closeMenu } = useMobileMenu();
  const menuRef = useRef(null);
  const previousFocusRef = useRef(null);

  // Manage focus on open/close
  useEffect(() => {
    if (isMobileMenuOpen) {
      previousFocusRef.current = document.activeElement;
      const firstFocusable = menuRef.current?.querySelector(FOCUSABLE_SELECTORS);
      firstFocusable?.focus();
    } else {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    }
  }, [isMobileMenuOpen]);

  // Focus trap + Escape to close
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeMenu();
        return;
      }
      if (e.key !== "Tab") return;

      const focusable = Array.from(
        menuRef.current?.querySelectorAll(FOCUSABLE_SELECTORS) ?? []
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMobileMenuOpen, closeMenu]);

  return (
    <div
      ref={menuRef}
      className={`mobile-menu ${isMobileMenuOpen ? "active" : ""}`}
      role="dialog"
      aria-modal={isMobileMenuOpen ? "true" : undefined}
      aria-label="Navigation menu"
      aria-hidden={isMobileMenuOpen ? undefined : true}
    >
      <div className="mobile-menu__header">
        <Link
          to="/"
          className="mobile-menu__brand"
          onClick={closeMenu}
          aria-label="Data Chimp Labs — go to homepage"
        >
          <img src={brandAssets.logoWhite} alt="" width="640" height="180" />
        </Link>
        <button
          type="button"
          className="mobile-menu__close"
          onClick={closeMenu}
          aria-label="Close navigation menu"
        >
          <X size={24} aria-hidden="true" />
        </button>
      </div>

      <div className="mobile-menu__content">
        <nav aria-label="Mobile navigation">
          <div className="mobile-menu__links">
            {siteMetadata.navigationLinks.map((link) => (
              <Link
                key={link.id}
                to={link.href}
                onClick={closeMenu}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </nav>

        <Link
          to="/#contact"
          className="mobile-menu__cta"
          onClick={closeMenu}
        >
          Talk to Us
        </Link>
      </div>
    </div>
  );
};

export default MobileMenu;
